// Product page behaviour: the copy clamp, the sticky add-to-cart bar and the
// mobile gallery counter.
//
// ponytail: three small custom elements. Each one degrades to something usable
// when it never upgrades - the copy shows in full, the bar stays away, the
// gallery is still a scroller.

import { CartLinesUpdateEvent } from '@shopify/events';

/** Clips long copy to a height and offers a toggle, once it knows it overflows. */
class AmajiMaxHeight extends HTMLElement {
  connectedCallback() {
    this.inner = this.querySelector('.amaji-clamp__inner');
    this.toggle = this.querySelector('.amaji-clamp__toggle');
    if (!this.inner || !this.toggle) return;
    this.toggle.addEventListener('click', this.#onToggle);
    // Measure after layout, or the copy has no height yet and never clamps.
    requestAnimationFrame(() => this.#measure());
  }

  #measure() {
    const limit = parseInt(getComputedStyle(this).getPropertyValue('--amaji-clamp-height'), 10) || 300;
    // Only clamp copy that actually overflows; a short description gets no
    // pointless "Read more".
    if (this.inner.scrollHeight > limit) this.setAttribute('data-clamped', '');
  }

  #onToggle = () => {
    const open = this.hasAttribute('data-open');
    this.toggleAttribute('data-open', !open);
    this.toggle.setAttribute('aria-expanded', String(!open));
  };
}

/** Slides a compact buy bar in once the real buy button has scrolled away. */
class AmajiStickyAtc extends HTMLElement {
  connectedCallback() {
    const buy = document.querySelector('.amaji-pdp__buy');
    if (!buy || !('IntersectionObserver' in window)) return;
    this.querySelector('.amaji-pdp__atc-bar-inner')?.addEventListener('click', () => {
      buy.scrollIntoView({ behavior: 'smooth', block: 'center' });
      buy.querySelector('button, [type="submit"]')?.focus({ preventScroll: true });
    });
    this.observer = new IntersectionObserver(
      // Only once the buy button has scrolled up past the top. Showing it whenever the button
      // was out of view put the bar over the swatches on load on a desktop, where the button
      // starts below the fold.
      ([entry]) => this.toggleAttribute('data-shown', !entry.isIntersecting && entry.boundingClientRect.top < 0),
      { rootMargin: '0px 0px -20% 0px' }
    );
    this.observer.observe(buy);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

/** Keeps the mobile gallery counter in step with the scroller. */
class AmajiGallery extends HTMLElement {
  connectedCallback() {
    const counter = this.parentElement?.querySelector('.amaji-pdp__counter-current');
    if (!counter) return;
    const slides = [...this.querySelectorAll('.amaji-pdp__slide')];
    this.addEventListener(
      'scroll',
      () => {
        const index = Math.round(this.scrollLeft / Math.max(this.clientWidth, 1));
        counter.textContent = String(Math.min(index + 1, slides.length));
      },
      { passive: true }
    );
    for (const button of this.parentElement.querySelectorAll('.amaji-pdp__gallery-button')) {
      button.addEventListener('click', () => {
        this.scrollBy({ left: this.clientWidth * Number(button.dataset.step), behavior: 'smooth' });
      });
    }
  }
}

for (const [name, ctor] of [
  ['amaji-max-height', AmajiMaxHeight],
  ['amaji-sticky-atc', AmajiStickyAtc],
  ['amaji-gallery', AmajiGallery],
]) {
  if (!customElements.get(name)) customElements.define(name, ctor);
}

/** A quantity stepper: the buttons drive the number field and fire `change`. */
class AmajiQuantity extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.amaji-pdp__quantity-input');
    if (!this.input) return;
    this.addEventListener('click', (event) => {
      const button = event.target.closest('.amaji-pdp__quantity-step');
      if (!button) return;
      const min = Number(this.input.min || 1);
      const next = Number(this.input.value || min) + Number(button.dataset.step);
      this.input.value = String(Math.max(min, next));
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}

if (!customElements.get('amaji-quantity')) customElements.define('amaji-quantity', AmajiQuantity);

/** Copy-link button on the share row. */
class AmajiSocialShare extends HTMLElement {
  connectedCallback() {
    const button = this.querySelector('.amaji-pdp__share-copy');
    if (!button || !navigator.clipboard) return;
    button.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(button.dataset.url);
        button.setAttribute('data-copied', '');
        setTimeout(() => button.removeAttribute('data-copied'), 2000);
      } catch {
        // Clipboard access can be refused; the link is still on screen.
      }
    });
  }
}

if (!customElements.get('amaji-social-share')) {
  customElements.define('amaji-social-share', AmajiSocialShare);
}

/**
 * The variant picker.
 *
 * Resolves a selection against the variant data embedded in the page rather
 * than re-rendering the section over the network: a click should change the
 * price and the image immediately, and it should keep working when the
 * connection does not. The radios carry the option names, so the form still
 * posts a resolvable selection if this never runs.
 */
class AmajiVariantSelects extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('.amaji-pdp__variant-data');
    if (!data) return;
    try {
      this.variants = JSON.parse(data.textContent);
    } catch (error) {
      console.error('variant data did not parse', error);
      return;
    }
    this.addEventListener('change', this.#onChange);
    // Reflect whatever is checked on load, so a ?variant= deep link and the
    // markup agree from the first paint.
    // On load the markup already shows the right variant, so reflect the
    // selection but leave the gallery in the order the merchant set it.
    this.#apply(this.#currentVariant(), { updateUrl: false, moveGallery: false });
  }

  /** @returns {string[]} the chosen value for each option, in option order. */
  #selection() {
    const chosen = [];
    for (const input of this.querySelectorAll('input:checked')) {
      chosen[Number(input.dataset.optionPosition || 1) - 1] = input.value;
    }
    return chosen;
  }

  #currentVariant() {
    const selection = this.#selection();
    return this.variants.find((variant) =>
      variant.options.every((option, index) => option === selection[index])
    );
  }

  #onChange = () => {
    const variant = this.#currentVariant();
    this.#apply(variant, { updateUrl: this.dataset.updateUrl === 'true', moveGallery: true });
  };

  #apply(variant, { updateUrl, moveGallery }) {
    const selection = this.#selection();
    const root = this.closest('.amaji-pdp') ?? document;

    // The value beside each option name.
    for (const fieldset of this.querySelectorAll('.amaji-pdp__option')) {
      const checked = fieldset.querySelector('input:checked');
      const label = fieldset.querySelector('.amaji-pdp__option-value');
      if (label) label.textContent = checked ? checked.value : '';
    }

    // Mark values that cannot be reached from the rest of the selection, so a
    // sold-out size reads as sold out rather than simply failing on submit.
    for (const input of this.querySelectorAll('input[data-option-position]')) {
      const index = Number(input.dataset.optionPosition) - 1;
      const candidate = selection.slice();
      candidate[index] = input.value;
      const match = this.variants.find((v) =>
        v.options.every((option, i) => (i === index || candidate[i] == null ? true : option === candidate[i])) &&
        v.options[index] === input.value
      );
      const available = Boolean(match && match.available);
      input.dataset.available = String(available);
      input.nextElementSibling?.classList.toggle('amaji-pdp__swatch--unavailable', !available);
    }

    if (!variant) {
      this.#setAddToCart(root, false, 'Unavailable');
      return;
    }

    const hidden = root.querySelector('.amaji-pdp__variant-id');
    if (hidden) hidden.value = String(variant.id);

    for (const amount of root.querySelectorAll('.amaji-pdp__price-now .amaji-pdp__amount')) {
      amount.textContent = variant.price;
    }
    const was = root.querySelector('.amaji-pdp__price-was .amaji-pdp__amount');
    if (was) {
      was.textContent = variant.compare_at_price ?? '';
      was.closest('.amaji-pdp__price-was')?.toggleAttribute('hidden', !variant.compare_at_price);
    }

    if (moveGallery && variant.image) this.#showImage(root, variant.image);

    this.#setAddToCart(root, variant.available, variant.available ? null : 'Sold out');

    if (updateUrl && variant.url) {
      history.replaceState({}, '', variant.url);
    }

    this.dispatchEvent(new CustomEvent('amaji:variant', { bubbles: true, detail: { variant } }));
  }

  #setAddToCart(root, enabled, label) {
    const button = root.querySelector('.amaji-pdp__add');
    if (!button) return;
    button.disabled = !enabled;
    const text = button.querySelector('.amaji-pdp__add-text');
    if (!text) return;
    if (enabled) {
      if (text.dataset.label) text.textContent = text.dataset.label;
    } else {
      if (!text.dataset.label) text.dataset.label = text.textContent.trim();
      if (label) text.textContent = label;
    }
  }

  /** Brings the variant's own photograph to the front of the gallery. */
  #showImage(root, src) {
    const gallery = root.querySelector('.amaji-pdp__gallery');
    if (!gallery) return;
    const file = src.split('?')[0].split('/').pop();
    const slide = [...gallery.querySelectorAll('.amaji-pdp__slide')].find((candidate) =>
      candidate.querySelector('img')?.src.includes(file)
    );
    if (!slide) return;
    if (getComputedStyle(gallery).overflowX === 'auto') {
      // The mobile gallery is a scroller: bring the variant's slide into view.
      gallery.scrollTo({ left: slide.offsetLeft - gallery.offsetLeft, behavior: 'smooth' });
    } else {
      // The desktop gallery is a collage, and the lead image is the one a
      // shopper reads first, so the chosen colour goes there. The image it
      // displaces takes the lead's old place rather than being dropped.
      const lead = gallery.querySelector('.amaji-pdp__slide--lead');
      if (lead && lead !== slide) {
        const leadImage = lead.querySelector('img');
        const slideImage = slide.querySelector('img');
        if (leadImage && slideImage) {
          const carried = { src: leadImage.src, srcset: leadImage.srcset, alt: leadImage.alt };
          leadImage.srcset = slideImage.srcset;
          leadImage.src = slideImage.src;
          leadImage.alt = slideImage.alt;
          slideImage.srcset = carried.srcset;
          slideImage.src = carried.src;
          slideImage.alt = carried.alt;
        }
      }
    }
    for (const other of gallery.querySelectorAll('.amaji-pdp__slide')) {
      other.classList.toggle('amaji-pdp__slide--current', other === slide);
    }
  }
}

if (!customElements.get('amaji-variant-selects')) {
  customElements.define('amaji-variant-selects', AmajiVariantSelects);
}

/** Sizes a select to its chosen option, so a long name does not shift the row. */
class AmajiResizeSelect extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    if (!this.select) return;
    this.select.addEventListener('change', () => {
      // Submitting on change means the control works before any of this loads.
      this.select.form?.submit();
    });
    this.#measure();
    this.select.addEventListener('change', () => this.#measure());
  }

  #measure() {
    const probe = document.createElement('span');
    probe.textContent = this.select.options[this.select.selectedIndex]?.text ?? '';
    const style = getComputedStyle(this.select);
    Object.assign(probe.style, {
      position: 'absolute',
      visibility: 'hidden',
      whiteSpace: 'pre',
      font: style.font,
      letterSpacing: style.letterSpacing,
    });
    document.body.append(probe);
    this.select.style.width = `${probe.offsetWidth + 24}px`;
    probe.remove();
  }
}

if (!customElements.get('amaji-resize-select')) {
  customElements.define('amaji-resize-select', AmajiResizeSelect);
}

/** A back-to-top button that slides in once the page has scrolled a screen. */
class AmajiBackToTop extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', () =>
      window.scrollTo({ top: 0, behavior: 'smooth' })
    );
    this.#update();
    window.addEventListener('scroll', this.#update, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#update);
  }

  #update = () => {
    this.toggleAttribute('data-shown', window.scrollY > window.innerHeight);
  };
}

if (!customElements.get('amaji-back-to-top')) {
  customElements.define('amaji-back-to-top', AmajiBackToTop);
}

/**
 * The add-to-cart form.
 *
 * Posts to the cart rather than navigating, so adding from a quick view leaves
 * the shopper where they were - which is the whole point of a quick view. With
 * no script the form still submits normally.
 */
class AmajiProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.form.addEventListener('submit', this.#onSubmit);
  }

  #onSubmit = async (event) => {
    event.preventDefault();
    const button = this.querySelector('.amaji-pdp__add');
    if (button?.getAttribute('aria-busy') === 'true') return;
    button?.setAttribute('aria-busy', 'true');
    this.#error('');

    const body = new FormData(this.form);

    // Ask the cart-add response for the sections Horizon's cart components morph
    // from, so the drawer and the count update without a page reload.
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) body.set('sections', [...new Set(sectionIds)].join(','));

    const quantity = Number(body.get('quantity')) || 1;
    const deferred = CartLinesUpdateEvent.createPromise();

    // Fire the event first (so listeners attach to the promise), then do the work.
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(body.get('id')), quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const addUrl = globalThis.Theme?.routes?.cart_add_url ?? '/cart/add.js';
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const added = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(added.description || added.message || 'Add to cart failed');
      }

      const cartUrl = globalThis.Theme?.routes?.cart_url ?? '/cart';
      const cart = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: added.items ?? [added],
          itemCount: cart.item_count,
          sections: added.sections,
          source: 'amaji-product-form',
          didError: false,
        },
      });

      // Close the quick view; Horizon's cart-drawer opens itself from the event.
      // Cart add-ons live inside the cart drawer's own <dialog>; closing that would hide the result.
      if (!this.hasAttribute('data-keep-dialog')) this.closest('dialog')?.close();
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.#error(error.message);
    } finally {
      button?.removeAttribute('aria-busy');
    }
  };

  #error(message) {
    const box = this.querySelector('.amaji-pdp__form-error');
    if (!box) return;
    const text = box.querySelector('.amaji-pdp__form-error-text') ?? box;
    text.textContent = message;
    box.toggleAttribute('hidden', !message);
  }
}

if (!customElements.get('amaji-product-form')) {
  customElements.define('amaji-product-form', AmajiProductForm);
}

// This theme's header renders a plain <span class="amaji-header__count"> rather
// than Horizon's <cart-icon>, so keep it in sync on any cart change.
document.addEventListener(CartLinesUpdateEvent.eventName, (event) => {
  event.promise
    ?.then(({ cart, detail }) => {
      const count = cart?.totalQuantity ?? detail?.itemCount;
      if (count == null) return;
      for (const el of document.querySelectorAll('.amaji-header__count')) el.textContent = count;
    })
    .catch(() => {});
});

/* Accordion row: opening one panel closes its siblings in the same stack. Native
   <details> keeps working with JS disabled; this only adds the single-open. */
class AmajiCollapsibleRow extends HTMLElement {
  connectedCallback() {
    const details = this.querySelector('details');
    if (!details) return;
    details.addEventListener('toggle', () => {
      if (!details.open) return;
      const stack = this.parentElement;
      if (!stack) return;
      for (const sibling of stack.querySelectorAll('amaji-collapsible-row > details[open]')) {
        if (sibling !== details) sibling.open = false;
      }
    });
  }
}

if (!customElements.get('amaji-collapsible-row')) {
  customElements.define('amaji-collapsible-row', AmajiCollapsibleRow);
}
